/**
 * Shadow System
 * Elevation and depth tokens with semantic shadow values for consistent layering
 */

// Branded type for shadow
declare const __shadow_brand: unique symbol;

export interface ShadowToken {
  readonly [__shadow_brand]: true;
  readonly _lightValue: string;
  readonly _darkValue: string;
}

// Factory function
const createShadowToken = (lightValue: string, darkValue?: string): ShadowToken => ({
  _lightValue: lightValue,
  _darkValue: darkValue || lightValue,
} as ShadowToken);

// Resolver function for shadow tokens
export const resolveShadow = (token: ShadowToken, isDarkMode: boolean = false): string => (isDarkMode ? token._darkValue : token._lightValue);

// Shadow definitions with light and dark mode variants
// Light mode uses subtle gray shadows, dark mode uses more prominent black shadows
export const Shadow = {
  // Elevation levels (increasing depth)
  none: createShadowToken("none"),

  subtle: createShadowToken(
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 2px 0 rgba(0, 0, 0, 0.3)",
  ),

  small: createShadowToken(
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)",
  ),

  medium: createShadowToken(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.4)",
  ),

  large: createShadowToken(
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.6), 0 4px 6px -2px rgba(0, 0, 0, 0.5)",
  ),

  extraLarge: createShadowToken(
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.7), 0 10px 10px -5px rgba(0, 0, 0, 0.6)",
  ),

  huge: createShadowToken(
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.8)",
  ),

  // Special purpose shadows
  inset: createShadowToken(
    "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
    "inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)",
  ),

  // Focus and interaction shadows
  focus: createShadowToken(
    "0 0 0 3px rgba(59, 130, 246, 0.5)", // Blue focus ring
    "0 0 0 3px rgba(96, 165, 250, 0.6)", // Lighter blue for dark mode
  ),

  outline: createShadowToken(
    "0 0 0 1px rgba(0, 0, 0, 0.1)",
    "0 0 0 1px rgba(255, 255, 255, 0.2)",
  ),

  // State-based colored shadows
  success: createShadowToken(
    "0 0 0 3px rgba(34, 197, 94, 0.3)",
    "0 0 0 3px rgba(74, 222, 128, 0.4)",
  ),

  warning: createShadowToken(
    "0 0 0 3px rgba(251, 191, 36, 0.3)",
    "0 0 0 3px rgba(252, 211, 77, 0.4)",
  ),

  error: createShadowToken(
    "0 0 0 3px rgba(239, 68, 68, 0.3)",
    "0 0 0 3px rgba(248, 113, 113, 0.4)",
  ),

  // Semantic component shadows
  button: createShadowToken(
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 2px 0 rgba(0, 0, 0, 0.3)",
  ),

  buttonHover: createShadowToken(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.4)",
  ),

  card: createShadowToken(
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)",
  ),

  cardHover: createShadowToken(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.4)",
  ),

  modal: createShadowToken(
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.7), 0 10px 10px -5px rgba(0, 0, 0, 0.6)",
  ),

  dropdown: createShadowToken(
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.6), 0 4px 6px -2px rgba(0, 0, 0, 0.5)",
  ),

  tooltip: createShadowToken(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.4)",
  ),

  input: createShadowToken(
    "inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "inset 0 1px 2px 0 rgba(0, 0, 0, 0.2)",
  ),

  inputFocus: createShadowToken(
    "0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 0 0 3px rgba(96, 165, 250, 0.2), inset 0 1px 2px 0 rgba(0, 0, 0, 0.2)",
  ),
} as const;
